import React, { useCallback, useEffect, useState } from 'react';
import { ScanBarcode, Search as SearchIcon, RefreshCw, Moon, Sun, CheckCircle, AlertCircle, ChevronRight, ChevronDown } from 'lucide-react';
import ToasterWithMessage from '../../components/shared-components/toaster';
import {
  recommendBarcodeSyncAccounts,
  searchBarcodeTeams,
  searchListedEventsByTeam,
  getEventBarcodeInventory,
  startVlAccountCrawl,
  getVlAccountCrawlStatus,
  checkVlBarcodes,
  type BarcodeSyncRecommendResult,
  type BarcodeTeamSearchResult,
  type ListedEventByTeam,
  type CheckBarcodesResult,
  type CheckBarcodesSeat,
} from '../ManageTickets/api/vlListing.api';
import BarcodeSyncConfirmModal from '../ManageTickets/components/BarcodeSyncConfirmModal';
import styles from './BarcodesPage.module.css';

type SyncStatus = 'idle' | 'syncing' | 'started' | 'completed' | 'success' | 'failed';

type AccountSyncState = {
  status: SyncStatus;
  message?: string;
};

const STATUS_LABEL: Record<SyncStatus, string> = {
  idle: 'Not synced',
  syncing: 'In progress',
  started: 'Crawling…',
  completed: 'Crawl completed',
  success: 'Success',
  failed: 'Failed',
};

const TEAM_RESYNC_SOURCE = '__team__';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const formatEventDateParts = (value?: string | null): { date: string; time: string } => {
  if (!value) return { date: '—', time: '—' };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const asText = String(value).trim();
    return { date: asText.slice(0, 10) || '—', time: '—' };
  }
  return {
    date: parsed.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
};

/**
 * Barcodes dashboard — Seasons / Purchase toggle + confirm-gated sync.
 * Purchase tab shows whatever accounts exist in this VL env (VL_CRAWL_ACCOUNTS).
 */
const BarcodesPage: React.FC = () => {
  const { success, error: showError, info } = ToasterWithMessage();
  const [mode, setMode] = useState<'seasons' | 'purchase' | 'teams'>('seasons');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  const [teamQuery, setTeamQuery] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lookup, setLookup] = useState<BarcodeSyncRecommendResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const [purchaseList, setPurchaseList] = useState<BarcodeSyncRecommendResult['purchaseAccounts']>(
    [],
  );
  const [purchaseListLoading, setPurchaseListLoading] = useState(false);
  const [purchaseListError, setPurchaseListError] = useState<string | null>(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [recommendation, setRecommendation] = useState<BarcodeSyncRecommendResult | null>(null);
  const [recommendError, setRecommendError] = useState<string | null>(null);
  const [initialPurchaseIds, setInitialPurchaseIds] = useState<string[] | null>(null);
  const [syncByAccountId, setSyncByAccountId] = useState<Record<string, AccountSyncState>>({});

  const [teamHits, setTeamHits] = useState<BarcodeTeamSearchResult['teams']>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [listedEvents, setListedEvents] = useState<ListedEventByTeam[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventListQuery, setEventListQuery] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);
  const [inventoryByEventId, setInventoryByEventId] = useState<Record<string, CheckBarcodesResult>>({});
  const [inventoryLoadingByEventId, setInventoryLoadingByEventId] = useState<Record<string, boolean>>({});
  const [inventoryErrorByEventId, setInventoryErrorByEventId] = useState<Record<string, string>>({});
  const [inventoryBarcodeFilterByEventId, setInventoryBarcodeFilterByEventId] = useState<
    Record<string, 'all' | 'attached' | 'missing'>
  >({});
  const [resyncSourceId, setResyncSourceId] = useState<string | null>(null);

  const accountStatus = (accountId?: string | null): AccountSyncState => {
    if (!accountId) return { status: 'idle' };
    return syncByAccountId[String(accountId)] || { status: 'idle' };
  };

  const statusClass = (status: SyncStatus) => {
    if (status === 'syncing' || status === 'started') return styles.statusProgress;
    if (status === 'completed' || status === 'success') return styles.statusStarted;
    if (status === 'failed') return styles.statusFailed;
    return styles.statusIdle;
  };

  const loadPurchaseList = useCallback(async () => {
    setPurchaseListLoading(true);
    setPurchaseListError(null);
    try {
      const rec = await recommendBarcodeSyncAccounts({});
      setPurchaseList(rec.purchaseAccounts || []);
    } catch (e: any) {
      setPurchaseListError(e?.message || 'Failed to load purchase accounts');
      setPurchaseList([]);
    } finally {
      setPurchaseListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'purchase') {
      void loadPurchaseList();
    }
  }, [mode, loadPurchaseList]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (mode === 'purchase') {
        await loadPurchaseList();
      } else if (mode === 'teams') {
        if (selectedTeam) {
          setEventsLoading(true);
          const rec = await searchListedEventsByTeam(selectedTeam);
          setListedEvents(rec.events || []);
          await Promise.all(
            expandedEventIds.map((eventId) => loadEventInventory(eventId, true)),
          );
          setEventsLoading(false);
        } else if (teamQuery.trim()) {
          await onSearchTeam();
        }
      } else if (teamQuery.trim()) {
        await onSearchTeam();
      }
    } catch (e: any) {
      setLookupError(e?.message || 'Failed to refresh barcode data');
      setEventsLoading(false);
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const openConfirm = useCallback(
    async (ctx?: {
      eventName?: string;
      teamName?: string;
      eventId?: string;
      purchaseAccountIds?: string[];
    }) => {
      setShowConfirm(true);
      setRecommendLoading(true);
      setRecommendError(null);
      setRecommendation(null);
      setInitialPurchaseIds(ctx?.purchaseAccountIds?.length ? ctx.purchaseAccountIds : null);
      try {
        const rec = await recommendBarcodeSyncAccounts({
          eventName: ctx?.eventName,
          teamName: ctx?.teamName,
          eventId: ctx?.eventId,
        });
        setRecommendation(rec);
      } catch (e: any) {
        setRecommendError(e?.message || 'Failed to load accounts');
      } finally {
        setRecommendLoading(false);
      }
    },
    [],
  );

  const isAccountSyncing = Object.values(syncByAccountId).some(
    (state) => state.status === 'syncing' || state.status === 'started',
  );
  const teamResyncBusy = confirming || isAccountSyncing;
  const isResyncingSource = (sourceId: string) => teamResyncBusy && resyncSourceId === sourceId;

  const openTeamResync = (sourceId: string) => {
    if (!selectedTeam) {
      info('Select a team first');
      return;
    }
    const isTeamLevel = sourceId === TEAM_RESYNC_SOURCE;
    const targetEventId = isTeamLevel ? selectedEventId : sourceId;
    setResyncSourceId(sourceId);
    if (!isTeamLevel) setSelectedEventId(sourceId);
    const event = listedEvents.find((row) => row.eventId === targetEventId);
    void openConfirm({
      teamName: selectedTeam,
      eventName: event?.eventName || selectedTeam,
      eventId: targetEventId || undefined,
    });
  };

  const onSearchTeam = async () => {
    const q = teamQuery.trim();
    if (!q) {
      info('Enter a team name to search');
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setLookup(null);
    setTeamHits([]);
    try {
      if (mode === 'teams') {
        const rec = await searchBarcodeTeams(q);
        setTeamHits(rec.teams || []);
        if (!rec.teams?.length) {
          info('No matching teams in the barcode map');
        }
        return;
      }
      const rec = await recommendBarcodeSyncAccounts({ teamName: q, eventName: q });
      setLookup(rec);
      if (!rec.seasonsAccount && !rec.purchaseAccounts?.length) {
        info('No sync accounts found for that team');
      }
    } catch (e: any) {
      setLookupError(e?.message || 'Search failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const onSelectTeam = async (teamName: string) => {
    setSelectedTeam(teamName);
    setSelectedEventId('');
    setExpandedEventIds([]);
    setInventoryByEventId({});
    setInventoryLoadingByEventId({});
    setInventoryErrorByEventId({});
    setInventoryBarcodeFilterByEventId({});
    setResyncSourceId(null);
    setEventListQuery('');
    setEventsLoading(true);
    setListedEvents([]);
    try {
      const rec = await searchListedEventsByTeam(teamName);
      setListedEvents(rec.events || []);
      if (!rec.events?.length) {
        info('No listed events found for that team');
      }
    } catch (e: any) {
      setLookupError(e?.message || 'Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  };

  const loadEventInventory = async (eventId: string, force = false) => {
    if (!eventId) return;
    if (!force && inventoryByEventId[eventId]) return;
    setInventoryLoadingByEventId((prev) => ({ ...prev, [eventId]: true }));
    setInventoryErrorByEventId((prev) => {
      const next = { ...prev };
      delete next[eventId];
      return next;
    });
    try {
      const rec = await getEventBarcodeInventory(eventId);
      setInventoryByEventId((prev) => ({ ...prev, [eventId]: rec }));
    } catch (e: any) {
      setInventoryErrorByEventId((prev) => ({
        ...prev,
        [eventId]: e?.message || 'Failed to load inventory',
      }));
    } finally {
      setInventoryLoadingByEventId((prev) => ({ ...prev, [eventId]: false }));
    }
  };

  const toggleEvent = async (eventId: string) => {
    if (!eventId) return;
    const isOpen = expandedEventIds.includes(eventId);
    if (isOpen) {
      setExpandedEventIds((prev) => prev.filter((id) => id !== eventId));
      return;
    }
    setExpandedEventIds((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
    setSelectedEventId(eventId);
    await loadEventInventory(eventId);
  };

  const onConfirm = async (accountIds: string[]) => {
    setConfirming(true);
    setSyncByAccountId((prev) => {
      const next = { ...prev };
      for (const id of accountIds) {
        next[String(id)] = { status: 'syncing', message: 'Starting crawl…' };
      }
      return next;
    });
    try {
      const currentInventory = selectedEventId ? inventoryByEventId[selectedEventId] : null;
      const inventoryTpIds = (currentInventory?.seats || []).map((seat) => String(seat.tpId || '').trim()).filter(Boolean);
      const crawl = await startVlAccountCrawl({
        accountIds,
        ...(inventoryTpIds.length ? { ticketPlacementIds: inventoryTpIds } : {}),
      });
      setShowConfirm(false);
      const results = crawl.results || [];
      setSyncByAccountId((prev) => {
        const next = { ...prev };
        if (results.length) {
          for (const row of results) {
            next[String(row.accountId)] = {
              status: row.success ? 'syncing' : 'failed',
              message: row.success
                ? 'Crawling Victory Live…'
                : row.message || 'Crawl failed',
            };
          }
        } else {
          for (const id of accountIds) {
            next[String(id)] = {
              status: crawl.success ? 'syncing' : 'failed',
              message: crawl.message,
            };
          }
        }
        return next;
      });

      const startedAccountIds = (results.length ? results : [])
        .filter((row) => row.success)
        .map((row) => String(row.accountId));
      const pollIds = startedAccountIds.length ? startedAccountIds : accountIds.filter((id) => {
        const row = results.find((r) => String(r.accountId) === String(id));
        return !row || row.success;
      });

      if (!pollIds.length) {
        showError(crawl.message || 'Failed to start Victory Live account crawl');
        return;
      }

      let statusDone = false;
      for (let attempt = 0; attempt < 120; attempt += 1) {
        const status = await getVlAccountCrawlStatus(pollIds);
        setSyncByAccountId((prev) => {
          const next = { ...prev };
          for (const row of status.results) {
            next[String(row.accountId)] = {
              status:
                row.status === 'processing'
                  ? 'syncing'
                  : row.status === 'failed'
                    ? 'failed'
                    : 'completed',
              message: row.message || row.lastErrorMessage || row.crawlStatus || (
                row.status === 'processing' ? 'Crawling Victory Live…' : 'Crawl completed'
              ),
            };
          }
          return next;
        });
        if (status.complete) {
          statusDone = true;
          break;
        }
        await sleep(5000);
      }

      if (!statusDone) {
        setSyncByAccountId((prev) => {
          const next = { ...prev };
          for (const id of pollIds) {
            next[String(id)] = {
              status: 'failed',
              message: 'Timed out waiting for crawl completion',
            };
          }
          return next;
        });
        showError('Timed out while waiting for crawl completion');
        return;
      }

      if (selectedEventId) {
        const verification = await checkVlBarcodes(
          inventoryTpIds,
          { eventId: selectedEventId, persist: true, logVerification: true },
        );
        setInventoryByEventId((prev) => ({ ...prev, [selectedEventId]: verification }));
        const missing = verification.missingCount || 0;
        if (missing > 0) {
          setSyncByAccountId((prev) => {
            const next = { ...prev };
            for (const id of pollIds) {
              if (next[String(id)]?.status === 'failed') continue;
              next[String(id)] = {
                status: 'failed',
                message: `${missing} seat(s) missing barcodes on Victory Live`,
              };
            }
            return next;
          });
          showError(`${missing} seat(s) failed barcode verification`);
        } else {
          setSyncByAccountId((prev) => {
            const next = { ...prev };
            for (const id of pollIds) {
              if (next[String(id)]?.status === 'failed') continue;
              next[String(id)] = {
                status: 'success',
                message: `Success: all ${verification.total} seat(s) have barcodes`,
              };
            }
            return next;
          });
          success(`Barcode sync complete: all ${verification.total} seat(s) verified`);
        }
      } else {
        success(crawl.message || 'Crawl completed on Victory Live');
      }
    } catch (e: any) {
      setSyncByAccountId((prev) => {
        const next = { ...prev };
        for (const id of accountIds) {
          next[String(id)] = { status: 'failed', message: e?.message || 'Crawl failed' };
        }
        return next;
      });
      showError(e?.message || 'Crawl failed');
    } finally {
      setConfirming(false);
      setResyncSourceId(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Barcodes</h1>
          <p>Sync Seasons or Purchase accounts, or search listed inventory by team.</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.toggle}>
            <button
              type="button"
              className={mode === 'seasons' ? styles.toggleActive : styles.toggleBtn}
              onClick={() => setMode('seasons')}
            >
              Seasons
            </button>
            <button
              type="button"
              className={mode === 'purchase' ? styles.toggleActive : styles.toggleBtn}
              onClick={() => setMode('purchase')}
            >
              Purchase
            </button>
            <button
              type="button"
              className={mode === 'teams' ? styles.toggleActive : styles.toggleBtn}
              onClick={() => setMode('teams')}
            >
              Search Teams
            </button>
          </div>
          <div className={styles.iconButtons}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => void handleRefresh()}
              disabled={refreshing}
              title="Refresh"
              aria-label="Refresh barcode data"
            >
              <RefreshCw size={18} />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleToggleDarkMode}
              title={isDarkMode ? 'Light mode' : 'Dark mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
      </header>

      {mode === 'teams' ? (
        <section className={styles.panel}>
          <h2>Search Teams</h2>
          <p className={styles.help}>
            Search a team, then expand an event to review seat barcodes from Victory Live.
            If barcodes are missing, use Resync Account here — no need to leave this screen.
          </p>
          <div className={styles.searchRow}>
            <div className={styles.searchBox}>
              <SearchIcon size={16} />
              <input
                value={teamQuery}
                onChange={(e) => setTeamQuery(e.target.value)}
                placeholder="Search team (e.g. Braves, Dodgers)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void onSearchTeam();
                }}
              />
            </div>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => void onSearchTeam()}
              disabled={lookupLoading}
            >
              {lookupLoading ? 'Searching…' : 'Search Teams'}
            </button>
          </div>
          {lookupError && <p className={styles.error}>{lookupError}</p>}
          {!!teamHits.length && (
            <div className={styles.teamHits}>
              {teamHits.map((hit: BarcodeTeamSearchResult['teams'][number]) => (
                <button
                  key={hit.team}
                  type="button"
                  className={selectedTeam === hit.team ? styles.teamHitActive : styles.teamHit}
                  onClick={() => void onSelectTeam(hit.team)}
                >
                  <strong>{hit.team}</strong>
                  <span>
                    {hit.platform} · {hit.type === 'seasons' ? hit.seasonsAccountLabel || 'Seasons' : 'Purchase'}
                  </span>
                </button>
              ))}
            </div>
          )}
          {selectedTeam && (
            <>
              <div className={styles.selectedTeamBar}>
                <div>
                  <strong>{selectedTeam}</strong>
                  {(() => {
                    const hit = teamHits.find((row) => row.team === selectedTeam);
                    if (!hit) return null;
                    return (
                      <div className={styles.muted}>
                        {hit.platform} · {hit.type === 'seasons' ? hit.seasonsAccountLabel || 'Seasons' : 'Purchase'}
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => openTeamResync(TEAM_RESYNC_SOURCE)}
                  disabled={teamResyncBusy}
                  title="Resync the mapped Seasons or Purchase account for this team"
                >
                  <ScanBarcode size={14} />
                  {isResyncingSource(TEAM_RESYNC_SOURCE) ? 'Resyncing…' : 'Resync Account'}
                </button>
              </div>
              {eventsLoading && <p className={styles.muted}>Loading listed events…</p>}
              {!eventsLoading && !listedEvents.length && (
                <p className={styles.muted}>No listed events found for that team.</p>
              )}
              {!!listedEvents.length && (() => {
                const eventFilter = eventListQuery.trim().toLowerCase();
                const visibleEvents = eventFilter
                  ? listedEvents.filter((event) => {
                      const name = (event.eventName || '').toLowerCase();
                      const venue = (event.venue || '').toLowerCase();
                      return name.includes(eventFilter) || venue.includes(eventFilter);
                    })
                  : listedEvents;
                return (
                  <div className={styles.eventListWrap}>
                    <div className={styles.eventListToolbar}>
                      <span>
                        {visibleEvents.length} event{visibleEvents.length === 1 ? '' : 's'}
                      </span>
                      <div className={styles.searchBox}>
                        <SearchIcon size={16} />
                        <input
                          value={eventListQuery}
                          onChange={(e) => setEventListQuery(e.target.value)}
                          placeholder="Search events..."
                        />
                      </div>
                    </div>
                    {!visibleEvents.length ? (
                      <p className={styles.muted}>No events match that search.</p>
                    ) : (
                      <div className={styles.eventTableWrap}>
                        <table className={styles.eventTable}>
                          <thead>
                            <tr>
                              <th className={styles.chevronCol} aria-hidden="true" />
                              <th>Event Name</th>
                              <th>Venue</th>
                              <th>Event Date</th>
                              <th>Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleEvents.map((event) => {
                              const isExpanded = expandedEventIds.includes(event.eventId);
                              const { date, time } = formatEventDateParts(event.eventDate);
                              const inventory = inventoryByEventId[event.eventId];
                              const inventoryLoading = Boolean(inventoryLoadingByEventId[event.eventId]);
                              const inventoryError = inventoryErrorByEventId[event.eventId];
                              const inventoryBarcodeFilter =
                                inventoryBarcodeFilterByEventId[event.eventId] || 'all';
                              return (
                                <React.Fragment key={event.eventId}>
                                  <tr
                                    className={isExpanded ? styles.eventListRowOpen : styles.eventListRow}
                                    onClick={() => void toggleEvent(event.eventId)}
                                  >
                                    <td className={styles.chevronCol}>
                                      <button
                                        type="button"
                                        className={styles.expandBtn}
                                        aria-expanded={isExpanded}
                                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${event.eventName || 'event'}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void toggleEvent(event.eventId);
                                        }}
                                      >
                                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                      </button>
                                    </td>
                                    <td className={styles.eventNameCell}>{event.eventName || 'Event'}</td>
                                    <td>{event.venue || '—'}</td>
                                    <td>{date}</td>
                                    <td>{time}</td>
                                  </tr>
                                  {isExpanded && (
                                    <tr className={styles.nestedRow}>
                                      <td colSpan={5}>
                                        <div
                                          className={styles.nestedPanel}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {inventoryError && <p className={styles.error}>{inventoryError}</p>}
                                          {inventoryLoading && (
                                            <p className={styles.muted}>Loading inventory from Victory Live…</p>
                                          )}
                                          {inventory && !inventoryLoading && (
                                            <>
                                              <div className={styles.summary}>
                                                <span>
                                                  {inventory.total} seat{inventory.total === 1 ? '' : 's'} ·{' '}
                                                  {inventory.attachedCount} attached · {inventory.missingCount} not attached
                                                </span>
                                                <div className={styles.barcodeFilterRow}>
                                                  <button
                                                    type="button"
                                                    className={
                                                      inventoryBarcodeFilter === 'all'
                                                        ? styles.barcodeFilterActive
                                                        : styles.barcodeFilterBtn
                                                    }
                                                    onClick={() =>
                                                      setInventoryBarcodeFilterByEventId((prev) => ({
                                                        ...prev,
                                                        [event.eventId]: 'all',
                                                      }))
                                                    }
                                                  >
                                                    All
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className={
                                                      inventoryBarcodeFilter === 'attached'
                                                        ? styles.barcodeFilterActive
                                                        : styles.barcodeFilterBtn
                                                    }
                                                    onClick={() =>
                                                      setInventoryBarcodeFilterByEventId((prev) => ({
                                                        ...prev,
                                                        [event.eventId]: 'attached',
                                                      }))
                                                    }
                                                    title="Show seats with barcodes attached"
                                                  >
                                                    <CheckCircle size={14} className={styles.attached} />
                                                    Attached
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className={
                                                      inventoryBarcodeFilter === 'missing'
                                                        ? styles.barcodeFilterActive
                                                        : styles.barcodeFilterBtn
                                                    }
                                                    onClick={() =>
                                                      setInventoryBarcodeFilterByEventId((prev) => ({
                                                        ...prev,
                                                        [event.eventId]: 'missing',
                                                      }))
                                                    }
                                                    title="Show seats missing barcodes"
                                                  >
                                                    <AlertCircle size={14} className={styles.missing} />
                                                    Missing
                                                  </button>
                                                </div>
                                                {inventory.missingCount > 0 && (
                                                  <button
                                                    type="button"
                                                    className={styles.primaryBtn}
                                                    onClick={() => openTeamResync(event.eventId)}
                                                    disabled={teamResyncBusy}
                                                    title="Resync the mapped account so missing barcodes can be pulled from Victory Live"
                                                  >
                                                    <ScanBarcode size={14} />
                                                    {isResyncingSource(event.eventId) ? 'Resyncing…' : 'Resync Account'}
                                                  </button>
                                                )}
                                              </div>
                                              {(() => {
                                                const visibleSeats = (inventory.seats || []).filter(
                                                  (seat: CheckBarcodesSeat) => {
                                                    if (inventoryBarcodeFilter === 'attached') {
                                                      return Boolean(seat.barcodeAttached);
                                                    }
                                                    if (inventoryBarcodeFilter === 'missing') {
                                                      return !seat.barcodeAttached;
                                                    }
                                                    return true;
                                                  },
                                                );
                                                if (!inventory.seats.length) {
                                                  return (
                                                    <p className={styles.muted}>No listed inventory for that event.</p>
                                                  );
                                                }
                                                if (!visibleSeats.length) {
                                                  return (
                                                    <p className={styles.muted}>
                                                      {inventoryBarcodeFilter === 'attached'
                                                        ? 'No seats with barcodes attached.'
                                                        : 'No seats missing barcodes.'}
                                                    </p>
                                                  );
                                                }
                                                return (
                                                  <div className={styles.nestedTableWrap}>
                                                    <table className={styles.nestedTable}>
                                                      <thead>
                                                        <tr>
                                                          <th>Section</th>
                                                          <th>Row</th>
                                                          <th>Seat</th>
                                                          <th>Barcode</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {visibleSeats.map((seat: CheckBarcodesSeat) => (
                                                          <tr key={seat.tpId}>
                                                            <td>{seat.section || '—'}</td>
                                                            <td>{seat.row || '—'}</td>
                                                            <td>{seat.seat ?? '—'}</td>
                                                            <td
                                                              className={
                                                                String(seat.barcode || '').trim()
                                                                  ? styles.attached
                                                                  : styles.missing
                                                              }
                                                            >
                                                              {String(seat.barcode || '').trim() || 'Not attached'}
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                );
                                              })()}
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </section>
      ) : mode === 'seasons' ? (
        <section className={styles.panel}>
          <h2>Seasons accounts</h2>
          <p className={styles.help}>
            Search a team on the Seasons list, review the recommended account, then Confirm sync.
          </p>
          <div className={styles.searchRow}>
            <div className={styles.searchBox}>
              <SearchIcon size={16} />
              <input
                value={teamQuery}
                onChange={(e) => setTeamQuery(e.target.value)}
                placeholder="Search team (e.g. Atlanta Braves)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void onSearchTeam();
                }}
              />
            </div>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => void onSearchTeam()}
              disabled={lookupLoading}
            >
              {lookupLoading ? 'Searching…' : 'Search'}
            </button>
          </div>
          {lookupError && <p className={styles.error}>{lookupError}</p>}
          {lookup && (
            <div className={styles.card}>
              <div>
                <strong>{lookup.primaryTeam || teamQuery}</strong>
                <div className={styles.muted}>{lookup.reason}</div>
                {lookup.seasonsAccount ? (
                  <div className={styles.accountLine}>
                    {lookup.seasonsAccount.teamName || lookup.seasonsAccount.marketplaceName || 'Seasons'}
                  </div>
                ) : (
                  <div className={styles.muted}>No Seasons account available.</div>
                )}
                {lookup.seasonsAccount && (
                  <span className={`${styles.statusBadge} ${statusClass(accountStatus(lookup.seasonsAccount.accountId).status)}`}>
                    {STATUS_LABEL[accountStatus(lookup.seasonsAccount.accountId).status]}
                  </span>
                )}
              </div>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() =>
                  void openConfirm({
                    teamName: lookup.primaryTeam || teamQuery,
                    eventName: lookup.eventName || teamQuery,
                  })
                }
                disabled={accountStatus(lookup.seasonsAccount?.accountId).status === 'syncing'}
              >
                <ScanBarcode size={14} />{' '}
                {accountStatus(lookup.seasonsAccount?.accountId).status === 'syncing'
                  ? 'Syncing…'
                  : 'Sync Account'}
              </button>
            </div>
          )}
        </section>
      ) : (
        <section className={styles.panel}>
          <h2>Purchase accounts</h2>
          <p className={styles.help}>
            Shows whatever is configured in this env via <code>VL_CRAWL_ACCOUNTS</code> (stage/preview
            may differ from prod&apos;s TM / SeatGeek / Tickets.com / AXS / Paciolan).
          </p>
          {purchaseListLoading && <p className={styles.muted}>Loading purchase accounts…</p>}
          {purchaseListError && <p className={styles.error}>{purchaseListError}</p>}
          {!purchaseListLoading && !purchaseList.length && !purchaseListError && (
            <p className={styles.muted}>No Purchase accounts configured for this VL env.</p>
          )}
          <div className={styles.purchaseGrid}>
            {purchaseList.map((acct) => {
              const state = accountStatus(acct.accountId);
              return (
                <div key={acct.accountId} className={styles.card}>
                  <div className={styles.cardTop}>
                    <strong>{acct.managerName || acct.marketplaceName}</strong>
                    <span className={`${styles.statusBadge} ${statusClass(state.status)}`}>
                      {STATUS_LABEL[state.status]}
                    </span>
                  </div>
                  {state.message && state.status !== 'idle' && (
                    <div className={styles.statusMessage}>{state.message}</div>
                  )}
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={state.status === 'syncing'}
                    onClick={() =>
                      void openConfirm({ purchaseAccountIds: [String(acct.accountId)] })
                    }
                  >
                    <ScanBarcode size={14} /> {state.status === 'syncing' ? 'Syncing…' : 'Sync'}
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => void openConfirm()}
            disabled={!purchaseList.length || confirming}
          >
            {confirming ? 'Syncing…' : 'Sync all Purchase accounts (confirm)'}
          </button>
        </section>
      )}

      <BarcodeSyncConfirmModal
        open={showConfirm}
        loadingRecommend={recommendLoading}
        confirming={confirming}
        recommendation={recommendation}
        error={recommendError}
        title={mode === 'teams' ? 'Resync Account' : 'Sync Barcodes'}
        initialPurchaseIds={initialPurchaseIds}
        onClose={() => {
          if (!confirming) {
            setShowConfirm(false);
            setResyncSourceId(null);
          }
        }}
        onConfirm={onConfirm}
      />
    </div>
  );
};

export default BarcodesPage;
